import Grid from "@mui/material/Grid2";
import { intl } from "LocaleWrapper";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { FormGroup } from "@cofano/cck";
import CargoQuantityUnitSelect from "view/calculationmethods/components/select/CargoQuantityUnitSelect";
import PayableOverSelect from "view/calculationmethods/components/select/PayableOverSelect";
import FormField from "components/cck/FormField";
import { AgreementTypeEnum, CargoQuantityUnit, PayableOver } from "__generated__/graphql";
import WeightRangesForm from "view/calculationmethods/components/form/WeightRangesForm";
import { Fragment, ReactNode } from "react";
import NumberInput from "components/cck/NumberInput";
import type { CalculationMethodFormByTypeProps } from "@schemas/calculationmethods/schema";

type FormValues = {
	agreements: {
		type?: AgreementTypeEnum;
		calculationMethod: {
			cargoQuantityUnit?: CargoQuantityUnit;
			price?: number;
			payableOver?: PayableOver;
			preAgreed?: number;
			minimumPrice?: number;
			minimumQuantityPayable?: number;
			weightRanges?: {
				from?: number;
				to?: number;
				price?: number;
			}[];
		}
	}[];
}

type FormField = {
	id: string;
	render: () => ReactNode;
}

const CargoQuantityCalculationMethodForm = ({ index, currency, hiddenFields }: CalculationMethodFormByTypeProps) => {
	const { control, setValue, formState: { errors } } = useFormContext<FormValues>();

	const type = useWatch({ control, name: `agreements.${index}.type` });
	const cargoQuantityUnit = useWatch({ control, name: `agreements.${index}.calculationMethod.cargoQuantityUnit` });
	const payableOver = useWatch({ control, name: `agreements.${index}.calculationMethod.payableOver` });
	const minimumPrice = useWatch({ control, name: `agreements.${index}.calculationMethod.minimumPrice` });
	const minimumQuantityPayable = useWatch({ control, name: `agreements.${index}.calculationMethod.minimumQuantityPayable` });

	const CargoQuantityCalculationMethodFormFields: FormField[] = [
		{
			id: "calculationMethod.cargoQuantityUnit",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.cargoQuantityUnit`}>
						<FormGroup label={intl.formatMessage({ id: "CARGO_QUANTITY_UNIT" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.cargoQuantityUnit`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<CargoQuantityUnitSelect {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.price",
			render: () => cargoQuantityUnit && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.price`}>
						<FormGroup label={intl.formatMessage({ id: `PRICE_PER_${cargoQuantityUnit}` })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.price`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonBefore={currency.symbol} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.payableOver",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.payableOver`}>
						<FormGroup label={intl.formatMessage({ id: "PAYABLE_OVER" })} overflow="hidden" required>
							<Controller
								rules={{ required: true }}
								name={`agreements.${index}.calculationMethod.payableOver`}
								control={control}
								render={({ field }) => (
									<PayableOverSelect {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.preAgreed",
			render: () => payableOver === PayableOver.Preagreed && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.preAgreed`}>
						<FormGroup label={intl.formatMessage({ id: "ORDER_QUANTITY" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.preAgreed`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonAfter={intl.formatMessage({ id: "UNIT_TON" })} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.minimumPrice",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.minimumPrice`}>
						<FormGroup label={intl.formatMessage({ id: "MINIMUM_PRICE" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.minimumPrice`}
								control={control}
								render={({ field: { onChange } }) => (
									<NumberInput
										value={minimumPrice}
										onChange={(val) => {
											setValue(`agreements.${index}.calculationMethod.minimumQuantityPayable`, undefined);
											onChange(val);
										}}
										disabled={minimumQuantityPayable != null}
										addonBefore={currency.symbol} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.minimumQuantityPayable",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.minimumQuantityPayable`}>
						<FormGroup label={intl.formatMessage({ id: "MINIMUM_QUANTITY_PAYABLE" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.minimumQuantityPayable`}
								control={control}
								render={({ field: { onChange } }) => (
									<NumberInput
										value={minimumQuantityPayable}
										onChange={(val) => {
											setValue(`agreements.${index}.calculationMethod.minimumPrice`, undefined);
											onChange(val);
										}}
										disabled={minimumPrice != null}
										addonAfter={intl.formatMessage({ id: "UNIT_MTON" })} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.weightRanges",
			render: () => type === AgreementTypeEnum.WeightRange && (
				<WeightRangesForm
					index={index}
					currency={currency} />
			),
		},
	];

	return (
		<>
			{CargoQuantityCalculationMethodFormFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};



export default CargoQuantityCalculationMethodForm;
